/**
 * MODULE: _shared/frontend-shell/src/components/assistant/AssistantThinkingIndicator.tsx
 * PURPOSE: Shared “assistant is working” affordance — spinner + label + optional sub-status.
 * DEPENDENCIES: None (presentational)
 * PUBLIC_API: AssistantThinkingIndicator, AssistantThinkingIndicatorProps
 * SIDE_EFFECTS: None
 * AGENT SAFETY: 🟢 SAFE
 * FILE_SIZE: ~66 / 200 lines
 * RECENT: 2026-07-15 — Shared thinking indicator for AiAssistantPanel + AssistantPage (intents sheet UX).
 * See: _shared/frontend-shell/README.md, CHANGELOG.md § Unreleased
 */

export type AssistantThinkingIndicatorProps = {
  /** Primary status line, e.g. Thinking… or Processing… */
  label?: string;
  /** Secondary line (routing, tool name, etc.) */
  detail?: string;
  /** Defaults to ai-typing-indicator; AssistantPage uses loading-indicator */
  testId?: string;
  compact?: boolean;
};

export function AssistantThinkingIndicator({
  label = 'Thinking…',
  detail,
  testId = 'ai-typing-indicator',
  compact = false,
}: AssistantThinkingIndicatorProps) {
  return (
    <span
      data-testid={testId}
      className={[
        'flex flex-col gap-1',
        compact ? 'px-0 py-0' : 'px-0.5 py-0.5',
      ].join(' ')}
      aria-live="polite"
      aria-atomic="true"
      role="status"
    >
      <span
        className={[
          'inline-flex items-center font-medium text-gray-600 dark:text-gray-300',
          compact ? 'gap-1 text-[11px]' : 'gap-2 text-[12px]',
        ].join(' ')}
      >
        <span
          className={[
            'inline-block shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600 dark:border-gray-600 dark:border-t-primary-400',
            compact ? 'h-3 w-3' : 'h-3.5 w-3.5',
          ].join(' ')}
          aria-hidden
        />
        <span className="inline-flex items-center gap-0.5" aria-hidden>
          <span className="inline-block h-1 w-1 animate-bounce rounded-full bg-current [animation-delay:0ms]" />
          <span className="inline-block h-1 w-1 animate-bounce rounded-full bg-current [animation-delay:120ms]" />
          <span className="inline-block h-1 w-1 animate-bounce rounded-full bg-current [animation-delay:240ms]" />
        </span>
        <span>{label}</span>
      </span>
      {detail?.trim() ? (
        <span
          className={[
            'font-medium leading-snug text-gray-500 dark:text-gray-400',
            compact ? 'text-[10px]' : 'text-[11px]',
          ].join(' ')}
        >
          {detail.trim()}
        </span>
      ) : null}
    </span>
  );
}
